/**
 * Create a recurring transfer schedule — preview, then confirm.
 *
 * DRAFT. Phase 4 of the recurring-transfer epic, written before the job
 * has run anywhere. See the header of `HoaTransferScheduleList.tsx` for
 * why this is not routed yet.
 *
 * WHAT THE TWO STEPS MEAN HERE, because it is not the transfer dialog's
 * two steps wearing a different hat. There, a person confirms one
 * movement of money. Here they confirm a STANDING INSTRUCTION: after
 * this dialog closes, occurrences fire unattended, and a unique index —
 * not a person — is what stops one firing twice.
 *
 * So the confirmation step leads with the dates. `next_occurrences` is
 * the only place "I meant the 1st of the quarter, not the 1st of every
 * month" is visible before it becomes twelve transfers instead of four,
 * and it is the reason this dialog has a second step at all.
 *
 * Uses the generated client. Regenerated 2026-08-31 once the endpoints
 * were live; `openapi-generator` stays pinned at **7.19.0**, because any
 * other version renames every generated class.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import { Badge } from './common/Catalyst/badge';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import {
  formatCurrency,
  formatDate,
  getBankErrorParts,
} from './utils/bankHelpers';
import {
  TransferFrequency,
  TransferScheduleCreate,
  TransferSchedulePreviewReturn,
} from './clients/rust-api-client';

interface Props {
  open: boolean;
  onClose: () => void;
  hoaId: string;
  onCompleted: () => void;
}

interface AcctOption {
  id: string;
  accountNumber: string;
  purpose?: string | null;
  balance?: number | null;
}

const purposeLabel = (purpose?: string | null): string =>
  purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : 'Account';

const acctLabel = (a: AcctOption): string => {
  const base = `${purposeLabel(a.purpose)} — ${a.accountNumber}`;
  return a.balance !== null && a.balance !== undefined
    ? `${base} (${formatCurrency(a.balance)})`
    : base;
};

const ORDINALS = ['1st', '2nd', '3rd'];
const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const TransferScheduleDialog: React.FC<Props> = ({
  open,
  onClose,
  hoaId,
  onCompleted,
}) => {
  const [accounts, setAccounts] = useState<AcctOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [name, setName] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] =
    useState<TransferFrequency>('monthly');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [anchorMonth, setAnchorMonth] = useState('1');
  const [startsOn, setStartsOn] = useState(
    new Date().toISOString().slice(0, 10)
  );
  /** Empty means no end — the default, and what most schedules want. */
  const [endsOn, setEndsOn] = useState('');

  const [preview, setPreview] =
    useState<TransferSchedulePreviewReturn | null>(null);
  /**
   * The draft this sitting has already created, if any.
   *
   * `preview` PERSISTS a schedule, so without this every trip through
   * the form leaves another one behind — going Back to fix a typo used
   * to produce two rows from one intended schedule. Naming the draft on
   * the next preview revises it in place instead.
   *
   * Survives `setPreview(null)` on Back; cleared only when the dialog
   * resets.
   */
  const [draftId, setDraftId] = useState<string | null>(null);
  const [error, setError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setName('');
    setSourceId('');
    setDestId('');
    setAmount('');
    setFrequency('monthly');
    setDayOfMonth('1');
    setDayOfWeek('1');
    setAnchorMonth('1');
    setStartsOn(new Date().toISOString().slice(0, 10));
    setEndsOn('');
    setPreview(null);
    setDraftId(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    let cancelled = false;
    (async () => {
      setAccountsLoading(true);
      try {
        const resp = await bankService.getHoaAccountsLive({ hoaId });
        if (cancelled) return;
        setAccounts(
          resp.data.accounts.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            purpose: a.purpose,
            balance: a.current_balance,
          }))
        );
      } catch (err) {
        if (cancelled) return;
        const { body } = getBankErrorParts(err);
        setError({
          title: "Couldn't load accounts",
          message: body || 'Please try again.',
        });
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hoaId, reset]);

  const submitPreview = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Only the fields that belong to the chosen frequency are sent.
      // The backend REFUSES one that does not — `daily` plus a
      // day_of_month has two readings and no way to choose — so a stale
      // value is an error rather than a harmless extra.
      const transferScheduleCreate: TransferScheduleCreate = {
        // Revise the draft this sitting already made, rather than
        // stacking up a new one per attempt.
        draft_id: draftId ?? undefined,
        source_hoa_account_id: sourceId,
        dest_hoa_account_id: destId,
        name,
        amount,
        frequency,
        starts_on: startsOn,
        // Absent, not null: an omitted end means "runs until somebody
        // stops it", which is the default a person expects.
        ends_on: endsOn || undefined,
        day_of_week:
          frequency === 'weekly' ? Number(dayOfWeek) : undefined,
        day_of_month:
          frequency === 'monthly' || frequency === 'quarterly'
            ? Number(dayOfMonth)
            : undefined,
        anchor_month:
          frequency === 'quarterly' ? Number(anchorMonth) : undefined,
      };

      const resp = await bankService.previewTransferSchedule({
        hoaId,
        transferScheduleCreate,
      });
      setPreview(resp.data);
      setDraftId(resp.data.schedule_id);
    } catch (err) {
      const { status, body } = getBankErrorParts(err);
      setError({
        title:
          status === 400
            ? 'That schedule is not allowed'
            : "Couldn't preview the schedule",
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitConfirm = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      await bankService.confirmTransferSchedule({
        hoaId,
        id: preview.schedule_id,
      });
      onCompleted();
      onClose();
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setError({
        title: "Couldn't activate the schedule",
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canPreview =
    !!name.trim() && !!sourceId && !!destId && sourceId !== destId && !!amount;

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>
        {preview ? 'Confirm this schedule' : 'New recurring transfer'}
      </DialogTitle>
      <DialogDescription>
        {preview
          ? 'Once activated this runs on its own. Check the dates below.'
          : 'Move a fixed amount between two of this HOA’s accounts on a schedule.'}
      </DialogDescription>

      <DialogBody>
        {error && (
          <ShowAlert
            alertType="error"
            alertTitle={error.title}
            alertMessage={error.message}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline={true}
          />
        )}

        {accountsLoading ? (
          <LoadingSpinner />
        ) : preview ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text className="text-sm font-medium">From</Text>
                <Text className="tabular-nums">
                  {preview.source_account_number}
                </Text>
              </div>
              <div>
                <Text className="text-sm font-medium">To</Text>
                <Text className="tabular-nums">
                  {preview.dest_account_number}
                </Text>
              </div>
              <div>
                <Text className="text-sm font-medium">Amount</Text>
                <Text className="tabular-nums">
                  {formatCurrency(Number(preview.amount))}
                </Text>
              </div>
              <div>
                <Text className="text-sm font-medium">
                  Balance on the source account now
                </Text>
                <Text className="tabular-nums">
                  {preview.source_current_balance !== null &&
                  preview.source_current_balance !== undefined
                    ? formatCurrency(preview.source_current_balance)
                    : 'Unavailable'}
                </Text>
              </div>
            </div>

            {/*
              The balance above is CONTEXT, not a gate — a schedule set up
              on the 20th for the 1st is not judged on today's balance.
              Saying so matters: a manager who sees a balance below the
              amount and no error will otherwise assume something is
              broken.
            */}
            <Text className="text-sm">
              The balance is shown for context. It is checked again on the
              morning of each transfer, and a transfer is not sent if the
              account is short then.
            </Text>

            <div>
              <Text className="text-sm font-medium mb-1">
                The next transfers this would make
              </Text>
              <div className="flex flex-wrap gap-2">
                {preview.next_occurrences.map((d) => (
                  <Badge key={d} color="sky">
                    {formatDate(d)}
                  </Badge>
                ))}
              </div>
              <Text className="mt-2 text-sm">
                Check these are the dates you meant. After you activate
                this, transfers are sent without anyone confirming them.
              </Text>
              {/* Whether it ever stops is part of what somebody is
                  agreeing to here, and the five dates above cannot show
                  it — a schedule ending in a year looks identical to one
                  that never ends. */}
              <Text className="mt-1 text-sm font-medium">
                {endsOn
                  ? `It stops after ${formatDate(endsOn)}.`
                  : 'It has no end date, and runs until somebody pauses or ends it.'}
              </Text>
            </div>
          </div>
        ) : (
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Monthly reserve funding"
                />
              </Field>
              <Field>
                <Label>From</Label>
                <FieldPicker
                  label="From"
                  value={sourceId}
                  onChange={setSourceId}
                  placeholder="Select an account…"
                  options={accounts.map((a) => ({
                    value: a.id,
                    label: acctLabel(a),
                  }))}
                />
              </Field>
              <Field>
                <Label>To</Label>
                <FieldPicker
                  label="To"
                  value={destId}
                  onChange={setDestId}
                  placeholder="Select an account…"
                  options={accounts
                    .filter((a) => a.id !== sourceId)
                    .map((a) => ({ value: a.id, label: acctLabel(a) }))}
                />
              </Field>
              <Field>
                <Label>Amount</Label>
                <Input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="150.00"
                  inputMode="decimal"
                />
              </Field>
              <Field>
                <Label>How often</Label>
                <FieldPicker
                  label="How often"
                  value={frequency}
                  onChange={(v) => setFrequency(v as TransferFrequency)}
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                  ]}
                />
              </Field>

              {frequency === 'weekly' && (
                <Field>
                  <Label>Day of the week</Label>
                  <FieldPicker
                    label="Day of the week"
                    value={dayOfWeek}
                    onChange={setDayOfWeek}
                    options={WEEKDAYS.map((d, i) => ({
                      value: String(i + 1),
                      label: d,
                    }))}
                  />
                </Field>
              )}

              {(frequency === 'monthly' || frequency === 'quarterly') && (
                <Field>
                  <Label>Day of the month</Label>
                  <FieldPicker
                    label="Day of the month"
                    value={dayOfMonth}
                    onChange={setDayOfMonth}
                    options={Array.from({ length: 31 }, (_, i) => ({
                      value: String(i + 1),
                      label: String(i + 1),
                    }))}
                  />
                  {Number(dayOfMonth) > 28 && (
                    // The clamp, said plainly. Day 31 in February is the
                    // 28th, not the 1st of March — and somebody picking
                    // 31 deserves to know that before they find out.
                    <Text className="mt-1 text-sm">
                      In shorter months this runs on the last day —{' '}
                      {dayOfMonth === '31'
                        ? '28 or 29 February, 30 April'
                        : 'the last day of the month'}
                      . It never rolls into the following month.
                    </Text>
                  )}
                </Field>
              )}

              {frequency === 'quarterly' && (
                <Field>
                  <Label>Which month of each quarter</Label>
                  <FieldPicker
                    label="Which month of each quarter"
                    value={anchorMonth}
                    onChange={setAnchorMonth}
                    options={ORDINALS.map((label, i) => ({
                      value: String(i + 1),
                      label: `${label} month (${
                        [
                          'Jan, Apr, Jul, Oct',
                          'Feb, May, Aug, Nov',
                          'Mar, Jun, Sep, Dec',
                        ][i]
                      })`,
                    }))}
                  />
                </Field>
              )}

              <Field>
                <Label>Not before</Label>
                <Input
                  type="date"
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                />
                <Text className="mt-1 text-sm">
                  The earliest this may run. It does not set the day — a
                  monthly schedule for the 1st started mid-month first
                  runs on the next 1st.
                </Text>
              </Field>

              <Field>
                <Label>Stop after (optional)</Label>
                <Input
                  type="date"
                  value={endsOn}
                  min={startsOn}
                  onChange={(e) => setEndsOn(e.target.value)}
                />
                <Text className="mt-1 text-sm">
                  {endsOn
                    ? 'The last day it may run. A transfer due on this ' +
                      'day still goes; nothing runs after it.'
                    : 'Leave empty and this runs until somebody pauses ' +
                      'or ends it.'}
                </Text>
              </Field>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>

      <DialogActions>
        {preview ? (
          <>
            <Button plain onClick={() => setPreview(null)}>
              Back
            </Button>
            <Button
              color="sky"
              disabled={submitting}
              onClick={submitConfirm}
            >
              {submitting ? 'Activating…' : 'Activate schedule'}
            </Button>
          </>
        ) : (
          <>
            <Button plain onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="sky"
              disabled={!canPreview || submitting}
              onClick={submitPreview}
            >
              {submitting ? 'Checking…' : 'Continue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferScheduleDialog;
